import {
  EmojiEvents as TrophyIcon,
  InfoOutlined as InfoOutlinedIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import {
  SalesOverviewStats,
  SalesTeamSummary,
} from "../../../store/services/salesManagerService";
import { WorkItemTypeEnum } from "../../../store/types/work.types";
import { getWorkItemIcon } from "../../../utils/workItemUtils";
import AwardsList from "../AwardsList";
import FunFactsBand from "../FunFactsBand";
import ActivityTrendsChart from "../charts/ActivityTrendsChart";
import SegmentDonutChart from "../charts/SegmentDonutChart";
import {
  BACKLOG_TOOLTIP,
  CATEGORY_LABELS,
  formatPercent,
  getCompletionRateColor,
  getContactAttemptCount,
  getMotesbokningTotal,
  getPlaneringTotal,
  OVERDUE_TOOLTIP,
} from "../shared/breakdown";

const CONTACT_ATTEMPT_COLOR = "#9B6B00";
const CONTACT_ATTEMPT_TOOLTIP =
  "Kontaktförsök — antal gånger säljarna försökt nå kunder utan att lyckas. Ren ansträngningsmetrik, ingår inte i några totaler.";

interface OverviewTabProps {
  overview: SalesOverviewStats | undefined;
  teamSummary: SalesTeamSummary | undefined;
  isLoading: boolean;
}

// Liten komponent för att visa slutförandegrad per kategori under trendgrafen
const CompletionRateBar: React.FC<{
  label: string;
  rate: number;
  theme: Theme;
}> = ({ label, rate, theme }) => {
  const color = getCompletionRateColor(rate, theme);
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ color, minWidth: 56 }}
        >
          {formatPercent(rate)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={rate}
          sx={{
            flex: 1,
            minWidth: 60,
            height: 8,
            borderRadius: 4,
            bgcolor: alpha(theme.palette.grey[300], 0.3),
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              bgcolor: color,
            },
          }}
        />
      </Box>
    </Box>
  );
};

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  info?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, info, children }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        height: "100%",
      }}
    >
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            {info && (
              <Tooltip title={info} arrow placement="top">
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: "text.secondary",
                    cursor: "help",
                    "&:hover": { color: "primary.main" },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
};

const OverviewTab: React.FC<OverviewTabProps> = ({
  overview,
  teamSummary,
  isLoading,
}) => {
  const theme = useTheme();

  // Primär data-källa är teamSummary (som innehåller både breakdown och
  // trender), men vi faller tillbaka på overview om en endpoint skulle haka.
  const breakdown = teamSummary?.Breakdown ?? overview?.Breakdown;
  const awards = teamSummary?.Awards ?? overview?.Awards ?? [];
  const funFacts = teamSummary?.FunFacts ?? overview?.FunFacts;
  const trends = teamSummary?.ActivityTrends ?? [];

  if (isLoading || !breakdown) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Skeleton variant="rectangular" height={380} sx={{ borderRadius: 3 }} />
        </Grid>
        <Grid item xs={12} md={7}>
          <Skeleton variant="rectangular" height={380} sx={{ borderRadius: 3 }} />
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Segment-donut + Top performers */}
      <Grid item xs={12} md={5}>
        <SectionCard
          title="Fördelning per segment"
          subtitle="Aktiviteter per kundsegment i vald period"
          info={
            <Box sx={{ p: 0.5, maxWidth: 280 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                Segment i kundresan
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                Aktiviteter grupperas per var i resan kunden befinner sig.
              </Typography>
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Före:
                </Typography>{" "}
                <Typography variant="caption">
                  Suspekt / Prospekt, Pipeline
                </Typography>
              </Box>
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Nuvarande:
                </Typography>{" "}
                <Typography variant="caption">
                  Befintlig kund, Merförsäljning
                </Typography>
              </Box>
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Efter:
                </Typography>{" "}
                <Typography variant="caption">
                  Lämnar, Före detta
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Övrigt:
                </Typography>{" "}
                <Typography variant="caption">
                  Endast nät, Oklassificerad
                </Typography>
              </Box>
            </Box>
          }
        >
          <SegmentDonutChart breakdown={breakdown} height={240} />

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          >
            <TrophyIcon fontSize="small" color="warning" />
            Utmärkelser
          </Typography>
          <AwardsList awards={awards} />
        </SectionCard>
      </Grid>

      {/* Aktivitetstrend */}
      <Grid item xs={12} md={7}>
        <SectionCard
          title="Aktivitetstrend"
          subtitle="Schemalagda vs slutförda aktiviteter per dag"
        >
          <ActivityTrendsChart trends={trends} height={320} />
          <Box
            sx={{
              mt: 1,
              pt: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              sx={{ width: "100%" }}
            >
              <CompletionRateBar
                label={CATEGORY_LABELS.Communication}
                rate={getPlaneringTotal(breakdown).CompletionRate}
                theme={theme}
              />
              <CompletionRateBar
                label={CATEGORY_LABELS.Followup}
                rate={getMotesbokningTotal(breakdown).CompletionRate}
                theme={theme}
              />
            </Stack>
          </Box>
        </SectionCard>
      </Grid>

      {/* Fun facts-band */}
      {funFacts && (
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            <CardContent>
              <FunFactsBand funFacts={funFacts} />
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Varningsrad: backlog + unscheduled */}
      <Grid item xs={12}>
        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.04),
          }}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              divider={
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", md: "block" } }}
                />
              }
            >
              <Tooltip title={OVERDUE_TOOLTIP} arrow placement="top">
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flex={1}
                  sx={{ cursor: "help" }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.12),
                      color: theme.palette.error.main,
                    }}
                  >
                    <WarningIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {breakdown.Total.Total.Total.Overdue}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Overdue aktiviteter
                    </Typography>
                  </Box>
                </Stack>
              </Tooltip>
              <Tooltip title={BACKLOG_TOOLTIP} arrow placement="top">
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flex={1}
                  sx={{ cursor: "help" }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.main,
                    }}
                  >
                    <ScheduleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {breakdown.CarriedOverBacklog}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Backlog som släpas in i perioden
                    </Typography>
                  </Box>
                </Stack>
              </Tooltip>
              <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.main,
                  }}
                >
                  <ScheduleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {breakdown.UnscheduledCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktiviteter utan schemalagt datum
                  </Typography>
                </Box>
              </Stack>
              {getContactAttemptCount(breakdown.Total.Total) > 0 && (
                <Tooltip title={CONTACT_ATTEMPT_TOOLTIP} arrow placement="top">
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flex={1}
                    sx={{ cursor: "help" }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(CONTACT_ATTEMPT_COLOR, 0.12),
                        color: CONTACT_ATTEMPT_COLOR,
                      }}
                    >
                      {getWorkItemIcon(WorkItemTypeEnum.ContactAttempt)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {getContactAttemptCount(breakdown.Total.Total)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Kontaktförsök (ingår ej i total)
                      </Typography>
                    </Box>
                  </Stack>
                </Tooltip>
              )}
              {overview?.CompletedToday !== undefined && (
                <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                  <Chip
                    label={`${overview.CompletedToday} slutförda idag`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default OverviewTab;
